import React, { useState, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRocket, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { faGithub } from '@fortawesome/free-brands-svg-icons';
import { theme } from '../theme';
import ScrollReveal, { FloatingShape } from './ScrollReveal';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

const ProjectsSection = styled.section`
  padding: 6rem 2rem;
  background: ${theme.colors.neutral[50]};
  background-image: 
    radial-gradient(at 0% 0%, ${theme.colors.secondary[50]} 0px, transparent 50%),
    radial-gradient(at 100% 100%, ${theme.colors.primary[50]} 0px, transparent 50%);
  position: relative;
  overflow: hidden;
  scroll-margin-top: 80px;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${theme.gradients.professional};
    opacity: 0.8;
  }
  }

  @media (max-width: 768px) {
    padding: 4.5rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 3.5rem 1.25rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 768px) {
    padding: 0 1.25rem;
  }

  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const Title = styled.h2`
  text-align: center;
  font-size: clamp(2rem, 4vw, 3rem);
  font-family: ${theme.typography.fontFamily.heading};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.neutral[900]};
  margin-bottom: 1rem;
  position: relative;
  animation: ${fadeInUp} 0.6s ease-out;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -12px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: ${theme.gradients.professional};
    border-radius: ${theme.borderRadius.full};
  }
`;

const Subtitle = styled.p`
  text-align: center;
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.neutral[600]};
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const FilterSection = styled.div`
  margin-bottom: 3rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  align-items: center;

  @media (max-width: 768px) {
    margin-bottom: 2.5rem;
    gap: 1.25rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 2rem;
    gap: 1rem;
  }
`;

const SearchBar = styled.input`
  width: 100%;
  max-width: 500px;
  padding: 0.875rem 1.5rem;
  border: 2px solid ${theme.colors.neutral[200]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.neutral[900]};
  background: white;
  transition: all ${theme.transitions.base};
  min-height: 44px;
  -webkit-appearance: none;
  touch-action: manipulation;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[400]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[100]};
  }
  
  &::placeholder {
    color: ${theme.colors.neutral[400]};
  }

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 0.75rem 1.25rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem 1rem;
    font-size: 16px; /* Prevents zoom on iOS */
    border-width: 1.5px;
  }
`;

const FilterButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;

  @media (max-width: 768px) {
    justify-content: flex-start;
    gap: 0.5rem;
    width: 100%;
  }

  @media (max-width: 640px) {
    flex-wrap: nowrap;
    overflow-x: auto;
    padding-bottom: 0.25rem;
    margin: 0 -0.5rem;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    scrollbar-width: thin;
    scrollbar-color: ${theme.colors.primary[300]} transparent;

    &::-webkit-scrollbar {
      height: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: ${theme.colors.primary[300]};
      border-radius: ${theme.borderRadius.full};
    }
  }
`;

const FilterButton = styled.button`
  padding: 0.625rem 1.5rem;
  border: 2px solid ${props => props.$active ? 'transparent' : theme.colors.neutral[200]};
  border-radius: ${theme.borderRadius.full};
  background: ${props => props.$active ? theme.gradients.professional : 'white'};
  color: ${props => props.$active ? 'white' : theme.colors.neutral[700]};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.sm};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  position: relative;
  overflow: hidden;
  min-height: 44px;
  white-space: nowrap;
  touch-action: manipulation;
  flex-shrink: 0;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    transform: translateX(-100%);
    transition: transform 0.6s;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
    border-color: ${props => props.$active ? 'transparent' : theme.colors.primary[300]};
    color: ${props => props.$active ? 'white' : theme.colors.primary[600]};
    
    &::before {
      transform: translateX(100%);
    }
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    padding: 0.625rem 1.25rem;
    font-size: ${theme.typography.fontSize.xs};
    min-height: 40px;
  }

  @media (max-width: 480px) {
    padding: 0.5rem 1rem;
    font-size: ${theme.typography.fontSize.xs};
    min-height: 38px;
  }
`;

const ProjectCount = styled.p`
  text-align: center;
  color: ${theme.colors.neutral[600]};
  font-size: ${theme.typography.fontSize.sm};
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: ${theme.typography.fontSize.xs};
    margin-bottom: 1.25rem;
    padding: 0 1rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1rem;
    padding: 0 0.5rem;
  }
`;

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2.5rem;
  padding: 2rem 0;
  position: relative;
  z-index: 1;
  min-height: 600px; /* Prevents layout jump */

  @media (max-width: 1024px) {
    gap: 2rem;
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    min-height: auto;
    gap: 1.75rem;
    padding: 1.5rem 0 1rem;
  }

  @media (max-width: 480px) {
    gap: 1.25rem;
  }
`;

const ProjectCard = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.xl};
  overflow: hidden;
  box-shadow: ${theme.shadows.md};
  transition: all ${theme.transitions.slow};
  text-decoration: none;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  border: 1px solid ${theme.colors.neutral[200]};
  animation: ${fadeInUp} 0.6s ease-out ${props => props.index * 0.1}s both;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${theme.gradients.professional};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform ${theme.transitions.slow};
    z-index: 1;
  }

  &:hover {
    transform: translateY(-10px);
    box-shadow: ${theme.shadows['2xl']};
    border-color: ${theme.colors.primary[200]};
    
    &::before {
      transform: scaleX(1);
    }
  }

  @media (max-width: 480px) {
    border-radius: ${theme.borderRadius.lg};
  }
`;

const BadgeContainer = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  display: flex;
  gap: 0.5rem;
  z-index: 10;
`;

const Badge = styled.span`
  padding: 0.35rem 0.85rem;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.bold};
  background: ${props => props.$variant === 'featured' 
    ? theme.gradients.professional 
    : 'rgba(0, 0, 0, 0.7)'};
  color: white;
  box-shadow: ${theme.shadows.md};
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  
  &::before {
    content: ${props => props.$variant === 'featured' ? '"⭐"' : '""'};
  }
`;

const QuickActions = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transform: translateY(-10px);
  transition: all ${theme.transitions.base};
  z-index: 10;

  ${ProjectCard}:hover & {
    opacity: 1;
    transform: translateY(0);
  }

  @media (hover: none) {
    opacity: 1;
    transform: none;
  }

  @media (max-width: 768px) {
    top: 0.75rem;
    right: 0.75rem;
  }
`;

const QuickActionButton = styled.a`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1.5px solid ${theme.colors.neutral[200]};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.neutral[700]};
  text-decoration: none;
  font-size: 0.875rem;
  transition: all ${theme.transitions.base};
  box-shadow: ${theme.shadows.md};
  touch-action: manipulation;

  &:hover {
    background: ${theme.gradients.professional};
    border-color: transparent;
    color: white;
    transform: scale(1.15);
    box-shadow: ${theme.shadows.lg};
  }
  
  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
`;

const ProjectImage = styled.div`
  height: 250px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: ${theme.colors.neutral[100]};
  overflow: hidden;
  position: relative;
  transition: transform ${theme.transitions.base};
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.05) 100%);
  }
  
  ${ProjectCard}:hover & {
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    height: 220px;
  }

  @media (max-width: 480px) {
    height: 200px;
  }
`;

const ProjectContent = styled.div`
  padding: 1.75rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
    gap: 0.65rem;
  }
`;

const ViewDetailsButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.875rem 1.75rem;
  background: ${theme.gradients.professional};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  font-size: ${theme.typography.fontSize.sm};
  text-decoration: none;
  cursor: pointer;
  transition: all ${theme.transitions.base};
  align-self: flex-start;
  box-shadow: ${theme.shadows.md};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    transform: translateX(-100%);
    transition: transform 0.6s;
  }
  
  svg {
    transition: transform ${theme.transitions.base};
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.xl};
    color: white;
    
    &::before {
      transform: translateX(100%);
    }
    
    svg {
      transform: translateX(4px) rotate(-15deg);
    }
  }
  
  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    align-self: stretch;
    width: 100%;
    min-height: 44px;
  }

  @media (max-width: 480px) {
    font-size: ${theme.typography.fontSize.sm};
    padding: 0.875rem 1.5rem;
    min-height: 48px;
    margin-top: 0.75rem;
  }
`;

const ProjectTitle = styled.h3`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  font-family: ${theme.typography.fontFamily.heading};
  color: ${theme.colors.neutral[900]};
  margin: 0;
  line-height: ${theme.typography.lineHeight.tight};
  transition: color ${theme.transitions.base};
  
  ${ProjectCard}:hover & {
    background: ${theme.gradients.professional};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  @media (max-width: 768px) {
    font-size: ${theme.typography.fontSize.xl};
  }

  @media (max-width: 480px) {
    font-size: ${theme.typography.fontSize.lg};
    line-height: ${theme.typography.lineHeight.snug};
  }
`;

const ProjectDescription = styled.p`
  color: ${theme.colors.neutral[600]};
  margin: 0;
  line-height: ${theme.typography.lineHeight.relaxed};
  font-size: ${theme.typography.fontSize.base};
  flex-grow: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: 768px) {
    font-size: ${theme.typography.fontSize.sm};
    -webkit-line-clamp: 3;
  }

  @media (max-width: 480px) {
    font-size: ${theme.typography.fontSize.sm};
    line-height: ${theme.typography.lineHeight.normal};
    -webkit-line-clamp: 2;
  }
`;

const ProjectTechStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: auto;
  padding-top: 0.75rem;

  @media (max-width: 480px) {
    gap: 0.4rem;
    padding-top: 0.5rem;
  }
`;

const TechTag = styled.span`
  background: ${theme.colors.neutral[100]};
  padding: 0.4rem 0.9rem;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.neutral[700]};
  border: 1px solid ${theme.colors.neutral[200]};
  transition: all ${theme.transitions.base};
  
  ${ProjectCard}:hover & {
    background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.secondary[500]});
    color: white;
    border-color: transparent;
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.sm};
  }

  @media (max-width: 480px) {
    padding: 0.35rem 0.75rem;
    font-size: 0.7rem;
  }
`;

const MoreTechTag = styled.span`
  background: ${theme.gradients.professional};
  padding: 0.35rem 0.85rem;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.bold};
  color: white;
  border: none;
  cursor: pointer;
  transition: all ${theme.transitions.base};
  
  &:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow: ${theme.shadows.md};
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 4rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 0.75rem;
    margin-top: 3rem;
    padding: 0 1rem;
  }

  @media (max-width: 480px) {
    gap: 0.5rem;
    margin-top: 2.5rem;
    padding: 0 0.5rem;
  }
`;

const PaginationButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: 2px solid ${theme.colors.neutral[200]};
  border-radius: ${theme.borderRadius.lg};
  background: white;
  color: ${theme.colors.neutral[700]};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.sm};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${theme.gradients.professional};
    opacity: 0;
    transition: opacity ${theme.transitions.base};
  }
  
  & > * {
    position: relative;
    z-index: 1;
  }
  
  &:hover:not(:disabled) {
    color: white;
    border-color: ${theme.colors.primary[400]};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
    
    &::before {
      opacity: 1;
    }
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    background: ${theme.colors.neutral[100]};
    border-color: ${theme.colors.neutral[200]};
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: ${theme.shadows.sm};
  }
  
  @media (max-width: 768px) {
    padding: 0.625rem 1.25rem;
    font-size: ${theme.typography.fontSize.xs};
    min-height: 44px;
  }

  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
    padding: 0.5rem 1rem;
    min-height: 42px;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: 480px) {
    gap: 0.4rem;
  }
`;

const PageNumber = styled.button`
  width: 45px;
  height: 45px;
  border: 2px solid ${props => props.$active ? 'transparent' : theme.colors.neutral[200]};
  border-radius: ${theme.borderRadius.lg};
  background: ${props => props.$active ? theme.gradients.professional : 'white'};
  color: ${props => props.$active ? 'white' : theme.colors.neutral[700]};
  font-weight: ${theme.typography.fontWeight.bold};
  font-size: ${theme.typography.fontSize.sm};
  cursor: ${props => props.$active ? 'default' : 'pointer'};
  transition: all ${theme.transitions.base};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    inset: -4px;
    border: 2px solid ${theme.colors.primary[400]};
    border-radius: ${theme.borderRadius.lg};
    opacity: 0;
    transition: opacity ${theme.transitions.fast};
  }
  
  &:hover:not(:disabled) {
    background: ${props => props.$active ? theme.gradients.professional : theme.colors.primary[50]};
    border-color: ${props => props.$active ? 'transparent' : theme.colors.primary[300]};
    color: ${props => props.$active ? 'white' : theme.colors.primary[600]};
    transform: ${props => props.$active ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.$active ? 'none' : theme.shadows.sm};
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    
    &::after {
      opacity: ${props => props.$active ? '0' : '1'};
    }
  }
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: ${theme.typography.fontSize.xs};
    min-height: 40px;
  }

  @media (max-width: 480px) {
    width: 38px;
    height: 38px;
    font-size: 0.7rem;
    min-height: 38px;
  }
`;

const PageEllipsis = styled.span`
  color: ${theme.colors.neutral[400]};
  font-weight: ${theme.typography.fontWeight.bold};
  padding: 0 0.5rem;
`;

function Projects() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const PROJECTS_PER_PAGE = 6;

  const projects = [
    {
      id: 1,
      title: 'AlbaniTM Application',
      description: 'A cross-platform mobile application for tracking and managing the electric motor of an innovative skateboard, built with Flutter.',
      image: '/images/albanitm_logo.png',
      imageUrl: '/images/albanitm_logo.png',
      techStack: ['Flutter', 'Dart', 'Firebase', 'REST APIs', 'State Management', 'Mobile UI/UX'],
      category: 'Mobile',
      year: '2024',
      featured: true,
      demo: '#',
      github: 'https://github.com/kabilmbarki/Albanitm_application'
    },
    {
      id: 2,
      title: 'ESP32-CAM Face Detection & Emotion Analysis',
      description: 'An embedded system that captures facial images via HTTP, integrated with a separate module for real-time emotion recognition.',
      image: '/images/face_emotion/project_details.png',
      imageUrl: '/images/face_emotion/project_details.png',  
      techStack: ['ESP32-CAM', 'Python','HTTP', 'Machine Learning','OpenCV', 'Image Processing','C/C++'],
      category: 'Embedded',
      year: '2024',
      featured: true,
      demo: '#',
      github: '#'
    },
    {
      id: 3,
      title: 'Planty - Smart Agriculture Monitoring System',
      description: 'An IoT-based system for smart agriculture, monitoring plant health and automating care, recognized in a university competition.',
      image: '/images/planty/logo_planty.png',
      imageUrl: '/images/planty/logo_planty.png',
      techStack: ['ESP32', 'Arduino IDE', 'C++', 'IoT', 'DHT11 Sensor', 'Soil Moisture Sensor', 'Water Pump'],
      category: 'IoT',
      year: '2025',
      featured: true,
      demo: 'https://www.linkedin.com/posts/kabil-mbarki_smartagriculture-iot-agritech-activity-7344794214150418435-oivr?utm_source=share&utm_medium=member_desktop&rcm=ACoAAD4bRLEBNTLeipAFp5dhg5Y5I78_Qffm3RA',
      github: 'https://github.com/kabilmbarki/Planty---Smart-Agriculture-Monitoring-System'
    },
    {
      id: 4,
      title: 'Smart Inventory Management System',
      description: 'A comprehensive Flutter application for efficient stock management, integrated with QR codes and a Flask backend.',
      image: '/images/inventaire/inventaire.png',
      imageUrl: '/images/inventaire/inventaire.png',
      techStack: ['Flutter', 'Dart', 'Flask', 'Python', 'SQLite', 'RESTful API', 'QR Code Scanner', 'QR Code Generation', 'State Management', 'Charts'],
      category: 'Mobile',
      year: '2024',
      featured: false,
      demo: '#',
      github: 'https://github.com/kabilmbarki/Gestion-de-Stock---Syst-me-d-Inventaire-Intelligent'
    },
    {
      id: 6,
      title: 'Le Saphir Bleu Café Website',
      description: 'A modern React web application for "Le Saphir Bleu Café," featuring an intuitive menu and responsive design.',
      image: '/images/lesahirblue_cafe/logo_coffee.jpg',
      imageUrl: '/images/lesahirblue_cafe/logo_coffee.jpg',
      techStack: ['React 19', 'React Router', 'React Bootstrap', 'CSS Modules', 'React Icons', 'JavaScript', 'HTML', 'CSS'],
      category: 'Web',
      year: '2024',
      featured: false,
      demo: 'https://le-saphir-bleu.netlify.app',
      github: 'https://github.com/kabilmbarki/Le-Saphir-Bleu'
    },
    {
      id: 5,
      title: 'Smart Home Control App',
      description: 'A comprehensive Flutter application for complete home automation, environmental monitoring, and smart notifications, integrated with Firebase.',
      image: '/images/homeapp/lunchicon.png',
      imageUrl: '/images/homeapp/lunchicon.png',
      techStack: ['Flutter', 'Dart', 'Firebase', 'State Management', 'HTTP Requests', 'Local Notifications', 'Cross-Platform', 'IoT Integration'],
      category: 'IoT',
      year: '2024',
      featured: false,
      demo: '#',
      github: 'https://github.com/kabilmbarki/Smart-Home-Control-App'
    },
    {
      id: 7,
      title: 'Arduino Dual-Axis Solar Tracker',
      description: 'An Arduino-based solar tracker that automatically adjusts its position for maximum solar panel efficiency using LDRs and servo motors.',
      image: '/images/solartracker/solartracker.png',
      imageUrl: '/images/solartracker/solartracker.png',
      techStack: ['Arduino Nano', 'C/C++', 'LDRs', 'Servo Motors', '3D Printing', 'Embedded Systems', 'Sensor Integration', 'IoT'],
      category: 'Embedded',
      year: '2025',
      featured: false,
      demo: 'https://www.linkedin.com/posts/kabil-mbarki_solarenergy-renewableenergy-electronics-activity-7347674800519045121-b1dr?utm_source=share&utm_medium=member_desktop&rcm=ACoAAD4bRLEBNTLeipAFp5dhg5Y5I78_Qffm3RA',
      github: 'https://github.com/kabilmbarki/Solar-Tracker-Project.git'
    }
  ];

  const categories = ['All', 'Mobile', 'Web', 'IoT', 'Embedded'];

  // Filter and search logic
  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      const matchesCategory = activeFilter === 'All' || project.category === activeFilter;
      const matchesSearch = searchQuery === '' || 
        project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.techStack.some(tech => tech.toLowerCase().includes(searchQuery.toLowerCase()));
      
      return matchesCategory && matchesSearch;
    });
  }, [activeFilter, searchQuery, projects]);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, searchQuery]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredProjects.length / PROJECTS_PER_PAGE);
  const startIndex = (currentPage - 1) * PROJECTS_PER_PAGE;
  const endIndex = startIndex + PROJECTS_PER_PAGE;
  const currentProjects = filteredProjects.slice(startIndex, endIndex);

  // Scroll to top when page changes with offset for fixed header
  const scrollToProjects = () => {
    const projectsSection = document.getElementById('projects');
    if (projectsSection) {
      const headerOffset = 100; // Account for fixed header
      const elementPosition = projectsSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const goToPage = (page) => {
    setCurrentPage(page);
    // Small delay to ensure state updates before scroll
    setTimeout(scrollToProjects, 50);
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
      setTimeout(scrollToProjects, 50);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
      setTimeout(scrollToProjects, 50);
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show smart pagination with ellipsis
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }

    return pages;
  };

  const MAX_VISIBLE_TAGS = 5;

  return (
    <ProjectsSection id="projects">
      <FloatingShape $top="8%" $right="4%" $size="70px" $shape="circle" $gradient="linear-gradient(135deg, rgba(16,185,129,0.07), rgba(59,130,246,0.07))" $duration="14s" $delay="1s" />
      <FloatingShape $bottom="10%" $left="3%" $size="55px" $shape="rounded" $gradient="linear-gradient(135deg, rgba(217,70,239,0.06), rgba(245,158,11,0.06))" $duration="16s" $delay="4s" $rotation="30deg" />
      <Container>
        <ScrollReveal direction="flipX" duration="0.9s">
          <Title>My Projects</Title>
          <Subtitle>
            Explore my portfolio of innovative solutions across mobile, web, IoT, and embedded systems
          </Subtitle>
        </ScrollReveal>

        <FilterSection>
          <SearchBar
            type="text"
            placeholder="🔍 Search projects by name, description, or technology..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          
          <FilterButtons>
            {categories.map(category => (
              <FilterButton
                key={category}
                $active={activeFilter === category}
                onClick={() => setActiveFilter(category)}
              >
                {category}
              </FilterButton>
            ))}
          </FilterButtons>
        </FilterSection>

        <ProjectCount>
          {filteredProjects.length === projects.length 
            ? `Showing ${startIndex + 1}-${Math.min(endIndex, filteredProjects.length)} of ${projects.length} projects`
            : `Showing ${startIndex + 1}-${Math.min(endIndex, filteredProjects.length)} of ${filteredProjects.length} matching projects`}
        </ProjectCount>

        <ProjectGrid>
          {currentProjects.map((project, projectIndex) => {
            const directions = ['up', 'scale', 'blur', 'flipX', 'zoom', 'spring'];
            return (
            <ScrollReveal key={project.id} direction={directions[projectIndex % directions.length]} delay={`${projectIndex * 0.12}s`} duration="0.85s">
            <ProjectCard index={projectIndex}>
              {(project.featured || project.year) && (
                <BadgeContainer>
                  {project.featured && <Badge $variant="featured">Featured</Badge>}
                  {project.year && <Badge $variant="year">{project.year}</Badge>}
                </BadgeContainer>
              )}
              
              <QuickActions>
                {project.demo && project.demo !== '#' && (
                  <QuickActionButton 
                    href={project.demo} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    title="View Live Demo"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                  </QuickActionButton>
                )}
                {project.github && project.github !== '#' && (
                  <QuickActionButton 
                    href={project.github} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    title="View on GitHub"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FontAwesomeIcon icon={faGithub} />
                  </QuickActionButton>
                )}
              </QuickActions>

              <ProjectImage style={{ backgroundImage: `url(${project.imageUrl})` }} />
              <ProjectContent>
                <ProjectTitle>{project.title}</ProjectTitle>
                <ProjectDescription>{project.description}</ProjectDescription>
                <ProjectTechStack>
                  {project.techStack.slice(0, MAX_VISIBLE_TAGS).map((tech, index) => (
                    <TechTag key={index}>{tech}</TechTag>
                  ))}
                  {project.techStack.length > MAX_VISIBLE_TAGS && (
                    <MoreTechTag title={project.techStack.slice(MAX_VISIBLE_TAGS).join(', ')}>
                      +{project.techStack.length - MAX_VISIBLE_TAGS} more
                    </MoreTechTag>
                  )}
                </ProjectTechStack>
                <ViewDetailsButton to={`/project/${project.id}`}>
                  View Details
                  <FontAwesomeIcon icon={faRocket} />
                </ViewDetailsButton>
              </ProjectContent>
            </ProjectCard>
            </ScrollReveal>
            );
          })}
        </ProjectGrid>

        {filteredProjects.length === 0 && (
          <div style={{ textAlign: 'center', padding: '4rem 2rem', color: theme.colors.neutral[600] }}>
            <p style={{ fontSize: theme.typography.fontSize['2xl'], marginBottom: '1rem' }}>🔍</p>
            <p>No projects found matching your criteria.</p>
            <p style={{ marginTop: '0.5rem', fontSize: theme.typography.fontSize.sm }}>
              Try adjusting your filters or search query.
            </p>
          </div>
        )}

        {/* Pagination */}
        {filteredProjects.length > 0 && totalPages > 1 && (
          <PaginationContainer>
            <PaginationButton
              onClick={goToPrevPage}
              disabled={currentPage === 1}
              aria-label="Previous page"
            >
              <span>← Previous</span>
            </PaginationButton>

            <PageNumbers>
              {getPageNumbers().map((page, index) => (
                page === '...' ? (
                  <PageEllipsis key={`ellipsis-${index}`}>...</PageEllipsis>
                ) : (
                  <PageNumber
                    key={page}
                    $active={currentPage === page}
                    onClick={() => goToPage(page)}
                    aria-label={`Go to page ${page}`}
                    aria-current={currentPage === page ? 'page' : undefined}
                  >
                    {page}
                  </PageNumber>
                )
              ))}
            </PageNumbers>

            <PaginationButton
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              aria-label="Next page"
            >
              <span>Next →</span>
            </PaginationButton>
          </PaginationContainer>
        )}
      </Container>
    </ProjectsSection>
  );
}

// Removed PropTypes validation since we define projects data internally

export default Projects;


